<template>
  <div class="design-overview">
    <ReportSectionHeader
      section-title="Design Overview"
      :sizing-name="sizing?.title"
      :well-name="headerLocationOrWellName"
      :customer-name="sizing?.customer?.customer_name"
      :remove-page-break="removePageBreak"
      :date-generated="dateGenerated"
    />

    <div v-if="sizing?.notes" class="notes-section">
      <h3 class="notes-heading">Sizing Notes</h3>
      <hr class="notes-separator" />
      <p class="text-sm mb-4">
        {{ sizing?.notes }}
      </p>
      <hr class="notes-separator" />
    </div>

    <!-- Well Info-->
    <table class="report-table well-info-table">
      <tr class="header-row">
        <th>
          <icon :icon="['fas', 'oil-well']" />
          <span class="font-medium ml-1 mr-2">Well Info</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>
            {{ scenario.scenario?.title }}
          </th>
        </template>
      </tr>

      <!-- Casings -->
      <template
        v-if="
          scenarios.some(scenario => scenario?.wellProperties?.r_top_casing)
        "
      >
        <tr class="title-row">
          <td>Casing(s)</td>
          <td v-for="i in scenarios.length * 2" :key="i"></td>
        </tr>
        <tr>
          <td>Top Casing Size</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                {{
                  computeCasingLabel(
                    scenario.wellProperties?.r_top_casing,
                    getLargeLengthUnit("Top Casing Length.write.Feet").value
                      .label === LengthUnits.Meters.label
                  )
                }}
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Top Casing Length</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LengthUnits.Feet"
                  :target-unit="
                    getLargeLengthUnit('Top Casing Length.write.Feet')
                  "
                  :model-value="scenario.wellProperties?.top_casing_length_feet"
                  :model="LargeLengthUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <template
          v-if="
            scenarios.some(scenario => scenario.wellProperties?.r_bottom_casing)
          "
        >
          <tr>
            <td>Bottom Casing Size</td>
            <template
              v-for="scenario in scenarios"
              :key="scenario.scenario?.id"
            >
              <UseScenarioUnits
                v-slot="{ getLargeLengthUnit }"
                :scenario="scenario.scenario"
              >
                <td></td>
                <td>
                  {{
                    computeCasingLabel(
                      scenario.wellProperties?.r_bottom_casing,
                      getLargeLengthUnit("Bottom Casing Length.write.Feet")
                        .value.label === LengthUnits.Meters.label
                    )
                  }}
                </td>
              </UseScenarioUnits>
            </template>
          </tr>
          <tr>
            <td>Bottom Casing Length</td>
            <template
              v-for="scenario in scenarios"
              :key="scenario.scenario?.id"
            >
              <UseScenarioUnits
                v-slot="{ getLargeLengthUnit }"
                :scenario="scenario.scenario"
              >
                <td></td>
                <td>
                  <ConverterInput
                    :base-unit="LengthUnits.Feet"
                    :target-unit="
                      getLargeLengthUnit('Bottom Casing Length.write.Feet')
                    "
                    :model-value="
                      scenario.wellProperties?.bottom_casing_length_feet
                    "
                    :model="LargeLengthUnits.options()"
                    label=""
                    mode="read"
                  />
                </td>
              </UseScenarioUnits>
            </template>
          </tr>
        </template>
      </template>
      <!-- Tubings -->
      <template
        v-if="scenarios.some(scenario => scenario.wellProperties?.r_top_tubing)"
      >
        <tr class="title-row">
          <td>Tubing(s)</td>
          <td v-for="i in scenarios.length * 2" :key="i"></td>
        </tr>

        <template
          v-if="
            scenarios.some(scenario => scenario.wellProperties?.r_top_tubing)
          "
        >
          <tr>
            <td>Top Tubing Size</td>
            <template
              v-for="scenario in scenarios"
              :key="scenario.scenario?.id"
            >
              <UseScenarioUnits
                v-slot="{ getLargeLengthUnit }"
                :scenario="scenario.scenario"
              >
                <td></td>
                <td>
                  {{
                    computeTubingLabel(
                      scenario.wellProperties?.r_top_tubing,
                      getLargeLengthUnit("Top Tubing Length.write.Feet").value
                        .label === LengthUnits.Meters.label
                    )
                  }}
                </td>
              </UseScenarioUnits>
            </template>
          </tr>
          <tr>
            <td>Top Tubing Length</td>
            <template
              v-for="scenario in scenarios"
              :key="scenario.scenario?.id"
            >
              <UseScenarioUnits
                v-slot="{ getLargeLengthUnit }"
                :scenario="scenario.scenario"
              >
                <td></td>
                <td>
                  <ConverterInput
                    :base-unit="LengthUnits.Feet"
                    :target-unit="
                      getLargeLengthUnit('Top Tubing Length.write.Feet')
                    "
                    :model-value="
                      scenario.wellProperties?.top_tubing_length_feet
                    "
                    :model="LargeLengthUnits.options()"
                    label=""
                    mode="read"
                  />
                </td>
              </UseScenarioUnits>
            </template>
          </tr>
        </template>

        <template
          v-if="
            scenarios.some(scenario => scenario.wellProperties?.r_bottom_tubing)
          "
        >
          <tr>
            <td>Bottom Tubing Size</td>
            <template
              v-for="scenario in scenarios"
              :key="scenario.scenario?.id"
            >
              <UseScenarioUnits
                v-slot="{ getLargeLengthUnit }"
                :scenario="scenario.scenario"
              >
                <td></td>
                <td>
                  {{
                    computeTubingLabel(
                      scenario.wellProperties?.r_bottom_tubing,
                      getLargeLengthUnit("Bottom Tubing Length.write.Feet")
                        .value.label === LengthUnits.Meters.label
                    )
                  }}
                </td>
              </UseScenarioUnits>
            </template>
          </tr>
          <tr>
            <td>Bottom Tubing Length</td>
            <template
              v-for="scenario in scenarios"
              :key="scenario.scenario?.id"
            >
              <UseScenarioUnits
                v-slot="{ getLargeLengthUnit }"
                :scenario="scenario.scenario"
              >
                <td></td>
                <td>
                  <ConverterInput
                    :base-unit="LengthUnits.Feet"
                    :target-unit="
                      getLargeLengthUnit('Bottom Tubing Length.write.Feet')
                    "
                    :model-value="
                      scenario.wellProperties?.bottom_tubing_length_feet
                    "
                    :model="LargeLengthUnits.options()"
                    label=""
                    mode="read"
                  />
                </td>
              </UseScenarioUnits>
            </template>
          </tr>
        </template>

        <tr>
          <td>Total Tubing Length</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LengthUnits.Feet"
                  :target-unit="
                    getLargeLengthUnit('Top Tubing Length.write.Feet')
                  "
                  :model-value="
                    scenario.wellProperties?.top_tubing_length_feet +
                    scenario.wellProperties?.bottom_tubing_length_feet
                  "
                  :model="LargeLengthUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Intake Set Depth</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LengthUnits.Feet"
                  :target-unit="getLargeLengthUnit('Pump Depth.convert.Feet')"
                  :model-value="
                    scenario.calcs.equipmentLengths.bottomOfIntakesDepthFt.value
                  "
                  :model="LargeLengthUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
      </template>
      <!-- Perforations -->
      <template
        v-if="
          scenarios.some(
            scenario =>
              scenario.wellProperties?.top_of_perforations_measured_depth_feet
          )
        "
      >
        <tr class="title-row">
          <td>Top of Perfs</td>
          <td v-for="i in scenarios.length * 2" :key="i"></td>
        </tr>
        <tr>
          <td>Measured Depth</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LengthUnits.Feet"
                  :target-unit="getLargeLengthUnit('Top of Perfs.write.Feet')"
                  :model-value="
                    scenario.wellProperties
                      ?.top_of_perforations_measured_depth_feet
                  "
                  :model="LargeLengthUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Vertical Depth</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LengthUnits.Feet"
                  :target-unit="getLargeLengthUnit('Length.write.Feet')"
                  :model-value="
                    scenario.wellProperties
                      ?.top_of_perforations_true_vertical_depth_feet
                  "
                  :model="LargeLengthUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
      </template>

      <template
        v-if="
          scenarios.some(scenario => scenario.wellProperties?.oil_gravity_api)
        "
      >
        <tr class="title-row">
          <td>Fluid Properties</td>
          <td v-for="i in scenarios.length * 2" :key="i"></td>
        </tr>
        <tr v-if="!hideOilGasRowsForHpsWater">
          <td>
            {{
              getHpsOilApiLabel(scenarios[0]?.wellProperties?.hps_liquid_type)
            }}
          </td>
          <template
            v-for="{
              scenario,
              showOilRates
            } in gasDisplayFormattScenarioMapedScenarios"
            :key="scenario.scenario?.id"
          >
            <UseScenarioUnits
              v-slot="{ getDensityUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="showOilRates"
                  :base-unit="DensityUnits.API"
                  :target-unit="getDensityUnit('Oil Gravity.write.API')"
                  :model-value="scenario.wellProperties?.oil_gravity_api"
                  :model="DensityUnits.options()"
                  label=""
                  mode="read"
                />
                <div v-else>N/A</div>
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="!hideWaterRowsForHpsOil">
          <td>
            {{
              getHpsWaterGravityLabel(
                scenarios[0]?.wellProperties?.hps_liquid_type
              )
            }}
          </td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getDensityUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="DensityUnits.SpecificGravity"
                  :target-unit="
                    getDensityUnit('Water Gravity.write.Specific Gravity')
                  "
                  :model-value="scenario.wellProperties?.water_specific_gravity"
                  :model="DensityUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="!hideWaterRowsForHpsOil">
          <td>Water Salinity</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getConcentrationUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.correlations?.water_viscosity"
                  :base-unit="ConcentrationUnits.PartsPerMillion"
                  :target-unit="getConcentrationUnit()"
                  :model-value="scenario.correlations?.salinity_ppm"
                  :model="ConcentrationUnits.options()"
                  label=""
                  mode="read"
                />
                <span v-else>N/A</span>
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="!hideOilGasRowsForHpsWater">
          <td>Gas Specific Gravity</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getDensityUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="DensityUnits.SpecificGravity"
                  :target-unit="
                    getDensityUnit('Gas Gravity.write.Specific Gravity')
                  "
                  :model-value="
                    scenario.calcs?.fluidProperties?.gasSpecificGravity?.value
                  "
                  :model="DensityUnits.options()"
                  :decimal-places="3"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
      </template>

      <template v-if="scenarios.some(scenario => scenario.wellProperties)">
        <tr class="title-row">
          <td>Well Test Info</td>
          <td v-for="i in scenarios.length * 2" :key="i"></td>
        </tr>
        <tr
          v-if="!hideOilGasRowsForHpsWater && !hideOilRateRowsForHpsCondensate"
        >
          <td>Oil Rate</td>
          <template
            v-for="{
              scenario,
              showOilRates
            } in gasDisplayFormattScenarioMapedScenarios"
            :key="scenario.scenario?.id"
          >
            <UseScenarioUnits
              v-slot="{ getLiquidFlowUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="showOilRates"
                  :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                  :target-unit="getLiquidFlowUnit('Oil Rate.write.STB/D')"
                  :model-value="
                    scenario.calcs.wellTestFlowRates.value.oilFlowRateSTBD
                  "
                  :model="LiquidFlowRateUnits.options()"
                  label=""
                  mode="read"
                />
                <div v-else>N/A</div>
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="!hideWaterRowsForHpsOil">
          <td>
            {{
              getHpsSingleLiquidRateLabel(
                scenarios[0]?.wellProperties?.hps_liquid_type
              )
            }}
          </td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLiquidFlowUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                  :target-unit="getLiquidFlowUnit('Water Rate.write.STB/D')"
                  :model-value="
                    scenario.calcs.wellTestFlowRates.value.waterFlowRateSTBD
                  "
                  :model="LiquidFlowRateUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>
            {{
              getHpsTotalLiquidRateLabel(
                scenarios[0]?.wellProperties?.hps_liquid_type
              )
            }}
          </td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLiquidFlowUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                  :target-unit="
                    getLiquidFlowUnit('Total Liquid Rate.write.STB/D')
                  "
                  :model-value="
                    scenario.calcs.wellTestFlowRates.value.liquidFlowRateSTBD
                  "
                  :model="LiquidFlowRateUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="isSizingEspShore">
          <td>Productivity Index</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getProductivityFlowUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="ProductivityIndexUnits.BarrelDayPsi"
                  :model-value="scenario.calcs.productivityIndexStbdPsia.value"
                  :target-unit="
                    getProductivityFlowUnit(
                      'Productivity Index.convert.stb/d/psi'
                    )
                  "
                  :model="ProductivityIndexUnits.options()"
                  :decimal-places="4"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="!hideWaterRowsForHpsOil && !hideWaterCutRowsForHpsAmine">
          <td>Water Cut</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                :base-unit="PercentageUnits.Ratio"
                :target-unit="PercentageUnits.Percentage"
                :model-value="
                  scenario.calcs.wellTestFlowRates.value.waterCutRatio
                "
                :model="PercentageUnits.options()"
                label=""
                mode="read"
              />
            </td>
          </template>
        </tr>
        <tr v-if="!hideOilGasRowsForHpsWater">
          <td>Gas Rate</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getGasFlowUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="GasFlowRateUnits.CubicFeetPerDay"
                  :target-unit="getGasFlowUnit('Gas Rate.write.mscf/d')"
                  :model-value="
                    scenario.calcs.wellTestFlowRates.value.gasFlowRateSCFD
                  "
                  :model="GasFlowRateUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="!hideOilGasRowsForHpsWater">
          <td>Bubble Point</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getPressureUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                  :target-unit="
                    getPressureUnit('Bubble Point Pressure.convert.PSIA')
                  "
                  :model-value="
                    scenario.calcs.fluidProperties.fluidPropertiesAtReservoir
                      .value.bubblePointPressurePSIA
                  "
                  :model="PressureUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="isSizingEspShore">
          <td>Datum Point</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LengthUnits.Feet"
                  :target-unit="
                    getLargeLengthUnit('Datum Point (MD).write.Feet')
                  "
                  :model-value="
                    scenario.wellProperties
                      ?.well_test_datum_point_measured_depth_feet
                  "
                  :model="LargeLengthUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="isSizingEspShore">
          <td>Static Datum Pressure</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getPressureUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                  :target-unit="
                    getPressureUnit(
                      'Static Bottomhole Pressure at Datum [Pr].write.PSIA'
                    )
                  "
                  :model-value="
                    scenario.wellProperties
                      ?.well_test_static_bottom_hole_pressure_psia
                  "
                  :model="PressureUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="isSizingEspShore">
          <td>Producing Datum Pressure</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getPressureUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                  :target-unit="
                    scenario.wellProperties?.well_test_liquid_input_type ===
                    'productivity_index_water_cut'
                      ? getPressureUnit(
                          'Static Bottomhole Pressure at Datum [Pr].write.PSIA'
                        )
                      : getPressureUnit(
                          'Producing Bottomhole Pressure at Datum [Pwf].write.PSIA'
                        )
                  "
                  :model-value="
                    scenario.wellProperties
                      ?.well_test_producing_bottom_hole_pressure_psia
                  "
                  :model="PressureUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="isSizingEspShore">
          <td>Surface Temperature</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getTempUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="TemperatureUnits.Fahrenheit"
                  :target-unit="getTempUnit('Surface Temperature.write.F°')"
                  :model-value="
                    scenario.wellProperties?.surface_temperature_fahrenheit
                  "
                  :model="TemperatureUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-else>
          <td>HPS Inlet Temperature</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getTempUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="TemperatureUnits.Fahrenheit"
                  :target-unit="getTempUnit('HPS Inlet Temperature.write.F°')"
                  :model-value="
                    scenario.wellProperties
                      ?.hps_pump_inlet_temperature_fahrenheit
                  "
                  :model="TemperatureUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
      </template>
      <!-- Design Conditions -->
      <template
        v-if="
          scenarios.some(scenario => scenario.wellProperties) &&
          isSizingEspShore
        "
      >
        <tr class="title-row">
          <td>Design Conditions</td>
          <td v-for="i in scenarios.length * 2" :key="i"></td>
        </tr>

        <tr>
          <td>Casing Pressure</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getPressureUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                  :target-unit="getPressureUnit('Casing Pressure.write.PSIA')"
                  :model-value="
                    scenario.parameters?.bottom_casing_pressure_psia
                  "
                  :model="PressureUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Tubing Pressure</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getPressureUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                  :target-unit="getPressureUnit('Tubing Pressure.write.PSIA')"
                  :model-value="
                    scenario.parameters?.bottom_tubing_pressure_psia
                  "
                  :model="PressureUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Desired Rate</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLiquidFlowUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                  :target-unit="getLiquidFlowUnit(targetLiquidRateInputLabel)"
                  :model-value="
                    scenario.parameters?.target_liquid_rate_std_barrel_day
                  "
                  :model="LiquidFlowRateUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Pump Intake Pressure</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getPressureUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                  :target-unit="
                    getPressureUnit('Pump Intake Pressure.convert.PSIA')
                  "
                  :model-value="scenario.calcs?.pumpIntakePressurePSIA.value"
                  :model="PressureUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
      </template>
    </table>

    <!-- Theoretical Prod Data -->
    <div class="subheader"></div>
    <table class="report-table">
      <tr class="header-row">
        <th>
          <icon :icon="['far', 'oil-temperature']" />
          <span class="font-medium ml-1 mr-2">Theoretical Production Data</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>{{ scenario.scenario?.title }}</th>
        </template>
      </tr>
      <tr>
        <td>Operating Frequency</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="FrequencyUnits.Hertz"
              mode="read"
              :model="FrequencyUnits.options()"
              :model-value="scenario.calcs.adjustedPumpHzFrequency.value"
            />
          </td>
        </template>
      </tr>
      <tr v-if="isHps">
        <td>Operating RPM</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="GenericUnits.RevolutionsPerMinute"
              mode="read"
              :model="[GenericUnits.RevolutionsPerMinute]"
              :model-value="
                scenario.calcs.pumpCalculations.aggregate.operatingRPM.value
              "
            />
          </td>
        </template>
      </tr>
      <tr>
        <td>Fluid Rate</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLiquidFlowUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :target-unit="
                  getLiquidFlowUnit('Total Liquid Rate.write.STB/D')
                "
                :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                mode="read"
                :model="LiquidFlowRateUnits.options()"
                :model-value="
                  scenario.calcs.surfaceFlowRates.value.liquidFlowRateSTBD
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="!hideOilGasRowsForHpsWater">
        <td>Gas Rate</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getGasFlowUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="GasFlowRateUnits.CubicFeetPerDay"
                :target-unit="getGasFlowUnit('Gas Rate.write.mscf/d')"
                mode="read"
                :model="GasFlowRateUnits.options()"
                :model-value="
                  scenario.calcs.surfaceFlowRates.value.gasFlowRateSCFD
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="!hideOilGasRowsForHpsWater && !hideOilRateRowsForHpsCondensate">
        <td>Oil Rate</td>
        <template
          v-for="{
            scenario,
            showOilRates
          } in gasDisplayFormattScenarioMapedScenarios"
          :key="scenario.scenario?.id"
        >
          <UseScenarioUnits
            v-slot="{ getLiquidFlowUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                v-if="showOilRates"
                label=""
                :target-unit="getLiquidFlowUnit('Oil Rate.write.STB/D')"
                :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                mode="read"
                :model="LiquidFlowRateUnits.options()"
                :model-value="
                  scenario.calcs.surfaceFlowRates.value.oilFlowRateSTBD
                "
              />
              <div v-else>N/A</div>
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="!hideWaterRowsForHpsOil">
        <td>
          {{
            getHpsSingleLiquidRateLabel(
              scenarios[0]?.wellProperties?.hps_liquid_type
            )
          }}
        </td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLiquidFlowUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :target-unit="getLiquidFlowUnit('Water Rate.write.STB/D')"
                :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                mode="read"
                :model="LiquidFlowRateUnits.options()"
                :model-value="
                  scenario.calcs.surfaceFlowRates.value.waterFlowRateSTBD
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Tubing Pressure</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :target-unit="getPressureUnit('Tubing Pressure.write.PSIA')"
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                mode="read"
                :model="PressureUnits.options()"
                :model-value="scenario.parameters?.bottom_tubing_pressure_psia"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Casing Pressure</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :target-unit="getPressureUnit('Casing Pressure.write.PSIA')"
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                mode="read"
                :model="PressureUnits.options()"
                :model-value="scenario.parameters?.bottom_casing_pressure_psia"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Liquid Above Pump (MD)</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLargeLengthUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="LengthUnits.Feet"
                :target-unit="
                  getLargeLengthUnit('Liquid Above Pump (MD).convert.Feet')
                "
                mode="read"
                :model="LargeLengthUnits.options()"
                :model-value="
                  scenario.calcs.intakeCalculations.aggregate
                    .liquidAbovePumpMDFt.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Pump Discharge Pressure</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                :target-unit="getPressureUnit(pumpDischargePressureInputLabel)"
                mode="read"
                :model="PressureUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.aggregate
                    .producedDischargePressurePSIA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Shut-In Pressure @ Wellhead</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                :target-unit="
                  getPressureUnit('Shut-In Pressure @ Wellhead.convert.PSIA')
                "
                mode="read"
                :model="PressureUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.aggregate
                    .wellheadShutInBurstPressurePSIA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isHps">
        <td>Differential Pressure</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{
              getDifferentialPressureTargetUnit,
              getDifferentialPressureModel
            }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                :target-unit="getDifferentialPressureTargetUnit()"
                mode="read"
                :model="getDifferentialPressureModel().value"
                :model-value="
                  scenario.calcs.pumpCalculations.aggregate
                    .differentialPressurePSIA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Pump Intake Pressure</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :target-unit="
                  getPressureUnit('Pump Intake Pressure.convert.PSIA')
                "
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                mode="read"
                :model="PressureUnits.options()"
                :model-value="scenario.calcs.pumpIntakePressurePSIA.value"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="!hideOilGasRowsForHpsWater">
        <td>Free Gas Into Pump</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="PercentageUnits.Ratio"
              :target-unit="PercentageUnits.Percentage"
              mode="read"
              :model="PercentageUnits.options()"
              :model-value="
                scenario.calcs.fluidProperties
                  .fluidPropertiesAfterAllGasSeparationAtPump.value
                  .gasFlowVolumeRatio
              "
            />
          </td>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Sensor Pressure</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                mode="read"
                :target-unit="
                  getPressureUnit(
                    `Producing Bottomhole Pressure at Datum [Pwf].write.PSIA`
                  )
                "
                :model="PressureUnits.options()"
                :model-value="
                  scenario.calcs.wellFlowingPressureAtSensorPSIA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Datum Pressure</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                :target-unit="
                  getPressureUnit(
                    'Static Bottomhole Pressure at Datum [Pr].write.PSIA'
                  )
                "
                :model-value="
                  scenario.wellProperties
                    ?.well_test_static_bottom_hole_pressure_psia
                "
                :model="PressureUnits.options()"
                label=""
                mode="read"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Perf Pressure</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                :target-unit="
                  getPressureUnit(
                    `Producing Bottomhole Pressure at Datum [Pwf].write.PSIA`
                  )
                "
                mode="read"
                :model="PressureUnits.options()"
                :model-value="
                  scenario.calcs.wellFlowingPressureAtPerfsPSIA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="!isHps">
        <td>Total System Efficiency</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="PercentageUnits.Ratio"
              :target-unit="PercentageUnits.Percentage"
              mode="read"
              :model="[GenericUnits.Generic]"
              :model-value="
                scenario.calcs.surfaceDriveCalculations
                  .totalSystemEfficiencyRatio.value
              "
            />
          </td>
        </template>
      </tr>
      <tr v-if="!isHps">
        <td>Monthly System Power Cost</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td v-if="scenario.parameters?.cable_cost_kw_hr_usd > 0">
            <ConverterInput
              label=""
              :base-unit="GenericUnits.UnitedStatesDollar"
              mode="read"
              :model="[GenericUnits.UnitedStatesDollar]"
              :model-value="
                scenario.calcs.surfaceDriveCalculations.monthlySystemCostUSD
                  .value
              "
            />
          </td>
          <td v-else>N/A</td>
        </template>
      </tr>
    </table>

    <!-- Y Block -->
    <div class="subheader"></div>
    <table
      v-if="
        scenarios.some(
          s =>
            (s.equipment?.use_uphole_y_tool && s.equipment?.r_uphole_y_tool) ||
            (s.equipment?.use_downhole_y_tool && s.equipment?.r_downhole_y_tool)
        )
      "
      class="report-table"
    >
      <tr class="header-row">
        <th>
          <icon :icon="['far', 'screwdriver-wrench']" />
          <span class="font-medium ml-1 mr-2">Y Tools</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>{{ scenario.scenario?.title }}</th>
        </template>
      </tr>
      <tr>
        <td>Y Block Manufacturer / Series</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            {{
              computeYToolLabel(
                scenario.equipment?.r_uphole_y_tool ||
                  scenario.equipment?.r_downhole_y_tool
              ) || "—"
            }}
          </td>
        </template>
      </tr>
      <tr>
        <td>Bypass Tubing</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLargeLengthUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              {{
                computeTubingLabel(
                  scenario.calcs.orderedYToolBypassTubings?.value?.[0]
                    ?.r_tubing,
                  getLargeLengthUnit("Y Tool Bypass Tubing Length.write.Feet")
                    .value.label === LengthUnits.Meters.label
                )
              }}
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Bypass Tubing Length</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLargeLengthUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="LengthUnits.Feet"
                :target-unit="
                  getLargeLengthUnit('Y Tool Bypass Tubing Length.write.Feet')
                "
                :model-value="
                  scenario.calcs.equipmentLengths.yToolBypassTubingLengthFt
                    ?.value
                "
                :model="LargeLengthUnits.options()"
                mode="read"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Equipment Tubing</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLargeLengthUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              {{
                computeTubingLabel(
                  scenario.calcs.orderedYToolEquipmentTubings?.value?.[0]
                    ?.r_tubing,
                  getLargeLengthUnit(
                    "Y Block Equipment Tubing Length.write.Feet"
                  ).value.label === LengthUnits.Meters.label
                )
              }}
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Equipment Tubing Length</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLargeLengthUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="LengthUnits.Feet"
                :target-unit="
                  getLargeLengthUnit(
                    'Y Tool Equipment Tubing Length.write.Feet'
                  )
                "
                :model-value="
                  scenario.calcs.equipmentLengths.yToolEquipmentTubingLengthFt
                    ?.value
                "
                :model="LargeLengthUnits.options()"
                mode="read"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
    </table>

    <!-- Cables-->
    <div class="subheader"></div>
    <table v-if="isSizingEspShore" class="report-table">
      <tr class="header-row">
        <th>
          <icon :icon="['fas', 'lines-leaning']" />
          <span class="font-medium ml-1 mr-2">Cables</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>{{ scenario.scenario?.title }}</th>
        </template>
      </tr>
      <template
        v-for="cableIdx in Math.max(
          ...scenarios.map(
            scenario => scenario.calcs.orderedCables.value.length
          )
        )"
        :key="cableIdx"
      >
        <tr>
          <td>Description</td>
          <template v-for="scenario in scenarios" :key="scenario?.scenario?.id">
            <td></td>
            <td>
              <span v-if="scenario.calcs.orderedCables.value[cableIdx - 1]">{{
                scenario.calcs.cableCalculations.individual?.value[
                  scenario.calcs.orderedCables.value[cableIdx - 1]?.id
                ]?.cableHeader
              }}</span>
            </td>
          </template>
        </tr>

        <tr>
          <td>Cable Length</td>

          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.orderedCables.value[cableIdx - 1]"
                  label=""
                  :base-unit="LargeLengthUnits.Feet"
                  :model="LargeLengthUnits.options()"
                  :target-unit="getLargeLengthUnit('Feet')"
                  mode="read"
                  :model-value="
                    scenario.calcs.cableCalculations.individual.value[
                      scenario.calcs.orderedCables.value[cableIdx - 1]?.id
                    ]?.cableLengthFT
                  "
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Volt Loss / 1000ft</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getVoltageUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.orderedCables.value[cableIdx - 1]"
                  label=""
                  :base-unit="VoltageUnits.Volts"
                  :model="VoltageUnits.options()"
                  :target-unit="getVoltageUnit('Total Voltage.convert.V')"
                  mode="read"
                  :model-value="
                    scenario.calcs.cableCalculations.individual.value[
                      scenario.calcs.orderedCables.value[cableIdx - 1]?.id
                    ]?.voltLossPerThousandFTV
                  "
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>

        <tr>
          <td>Total Volt Loss</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getVoltageUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.orderedCables.value[cableIdx - 1]"
                  label=""
                  :base-unit="VoltageUnits.Volts"
                  :model="VoltageUnits.options()"
                  :target-unit="getVoltageUnit('Total Voltage.convert.V')"
                  mode="read"
                  :model-value="
                    scenario.calcs.cableCalculations.individual.value[
                      scenario.calcs.orderedCables.value[cableIdx - 1]?.id
                    ]?.totalVoltLossV
                  "
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>

        <tr>
          <td>Cable Motor Load</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.orderedCables.value[cableIdx - 1]"
                label=""
                :base-unit="PercentageUnits.Ratio"
                :model="PercentageUnits.options()"
                :target-unit="PercentageUnits.Percentage"
                mode="read"
                :model-value="
                  scenario.calcs.cableCalculations.individual.value[
                    scenario.calcs.orderedCables.value[cableIdx - 1]?.id
                  ]?.cableMotorLoadRatio
                "
              />
            </td>
          </template>
        </tr>

        <tr>
          <td>Cable Temperature</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getTempUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.orderedCables.value[cableIdx - 1]"
                  label=""
                  :base-unit="TemperatureUnits.Fahrenheit"
                  :model="TemperatureUnits.options()"
                  :target-unit="
                    getTempUnit('Motor Winding Temperature.convert.F°')
                  "
                  mode="read"
                  :model-value="
                    scenario.calcs.cableCalculations.individual.value[
                      scenario.calcs.orderedCables.value[cableIdx - 1]?.id
                    ]?.cableMaxTemperatureF
                  "
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>

        <tr>
          <td>Cable Pwr Cost / Mo</td>
          <template v-for="scenario in scenarios" :key="scenario?.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.orderedCables.value[cableIdx - 1]"
                label=""
                :base-unit="GenericUnits.UnitedStatesDollar"
                mode="read"
                :model="[GenericUnits.UnitedStatesDollar]"
                :model-value="
                  scenario.calcs.cableCalculations.individual.value[
                    scenario.calcs.orderedCables.value[cableIdx - 1]?.id
                  ]?.cablePowerCostPerMonthUSD
                "
              />
            </td>
          </template>
        </tr>

        <tr></tr>
      </template>
    </table>

    <!-- Bolt On Discharge -->
    <div class="subheader"></div>
    <table
      v-if="scenarios.some(scenario => scenario.equipment?.r_bolt_on_discharge)"
      class="report-table"
    >
      <tr class="header-row">
        <th>
          <icon :icon="['fas', 'triangle']" />
          <span class="font-medium ml-1 mr-2">Bolt On Discharge</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>
            {{ scenario.scenario?.title }}
          </th>
        </template>
      </tr>
      <tr>
        <td>Manufacturer Series/Model</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            {{
              computeBoltOnDischargeLabel(
                scenario.equipment?.r_bolt_on_discharge
              )
            }}
          </td>
        </template>
      </tr>
      <tr>
        <td>Length</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLargeLengthUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                v-if="
                  isBoltOnDischargeMeasurementKnown(
                    scenario.equipment?.r_bolt_on_discharge?.length_inches
                  )
                "
                :base-unit="LengthUnits.Feet"
                :target-unit="
                  getLargeLengthUnit('Equipment Length.convert.Feet')
                "
                :model-value="
                  scenario.calcs.equipmentLengths.boltOnDischargeLengthFt?.value
                "
                :model="LargeLengthUnits.options()"
                label=""
                mode="read"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Weight</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getMassUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                v-if="
                  isBoltOnDischargeMeasurementKnown(
                    scenario.equipment?.r_bolt_on_discharge?.weight_pounds
                  )
                "
                :base-unit="MassUnits.Pounds"
                :target-unit="getMassUnit()"
                :model-value="
                  scenario.calcs.equipmentWeights.boltOnDischargeWeightLbs
                    ?.value
                "
                :model="MassUnits.options()"
                label=""
                mode="read"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
    </table>

    <!-- Pumps -->
    <table class="report-table">
      <tr class="header-row">
        <th>
          <icon :icon="['fas', 'oil-well']" />
          <span class="font-medium ml-1 mr-2">Pumps</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>
            {{ scenario.scenario?.title }}
          </th>
        </template>
      </tr>

      <template
        v-for="pumpIdx in Math.max(
          ...scenarios.map(
            scenario => scenario.calcs.descendingOrderPumps.value.length
          )
        )"
        :key="pumpIdx"
      >
        <tr>
          <td>Name</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td class="font-semibold">
              <span
                v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                class="p-1"
                >{{
                  computePumpLabel(
                    scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]
                      .r_pump,
                    scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]
                      .use_high_strength_shaft
                  )
                }}</span
              >
            </td>
          </template>
        </tr>
        <tr>
          <td>Stage Type</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <span
                v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                class="p-1"
                >{{
                  computePumpStageType(
                    scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]
                      .r_pump
                  )
                }}</span
              >
            </td>
          </template>
        </tr>
        <tr>
          <td>Stages</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <span class="p-1 font-monospace">{{
                scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]
                  ?.total_stages
              }}</span>
            </td>
          </template>
        </tr>
        <tr>
          <td>Q at Intake</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLiquidFlowUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                  label=""
                  :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                  mode="read"
                  :target-unit="getLiquidFlowUnit(targetLiquidRateInputLabel)"
                  :model="LiquidFlowRateUnits.options()"
                  :model-value="
                    scenario.calcs.pumpCalculations.individual.value[
                      scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                    ]?.intakeStageCalculations?.intakeFluidPropertiesContext
                      .flowRatesInSitu?.fluidFlowRateBBLD
                  "
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Q at Discharge</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLiquidFlowUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                  label=""
                  :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
                  mode="read"
                  :target-unit="getLiquidFlowUnit(targetLiquidRateInputLabel)"
                  :model="LiquidFlowRateUnits.options()"
                  :model-value="
                    scenario.calcs.pumpCalculations.individual.value[
                      scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                    ]?.dischargeStageCalculations
                      ?.dischargeFluidPropertiesContext.flowRatesInSitu
                      ?.fluidFlowRateBBLD
                  "
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr>
          <td>Pump Efficiency</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                label=""
                :base-unit="PercentageUnits.Ratio"
                mode="read"
                :target-unit="PercentageUnits.Percentage"
                :model="PercentageUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.individual.value[
                    scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                  ]?.efficiencyRatio
                "
              />
            </td>
          </template>
        </tr>
        <tr>
          <td>Percent of BEP</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                label=""
                :base-unit="PercentageUnits.Ratio"
                mode="read"
                :target-unit="PercentageUnits.Percentage"
                :model="PercentageUnits.options()"
                :model-value="getPumpBepRatio(scenario, pumpIdx)"
              />
            </td>
          </template>
        </tr>
        <tr v-if="isHps">
          <td>Max Head</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                  label=""
                  :base-unit="LengthUnits.Feet"
                  mode="read"
                  :target-unit="getLargeLengthUnit('Feet')"
                  :model="LargeLengthUnits.options()"
                  :model-value="
                    scenario.calcs.pumpCalculations.individual.value[
                      scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                    ]?.maxHeadFeet
                  "
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="isHps">
          <td>Operating Head</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getLargeLengthUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                  label=""
                  :base-unit="LengthUnits.Feet"
                  mode="read"
                  :target-unit="getLargeLengthUnit('Feet')"
                  :model="LargeLengthUnits.options()"
                  :model-value="
                    scenario.calcs.pumpCalculations.individual.value[
                      scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                    ]?.operatingHeadFeet
                  "
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="!hideOilGasRowsForHpsWater">
          <td>% Free Gas at Inlet</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                label=""
                :base-unit="PercentageUnits.Ratio"
                :target-unit="PercentageUnits.Percentage"
                mode="read"
                :model="PercentageUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.individual.value[
                    scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                  ]?.gasAtIntakeRatio
                "
              />
            </td>
          </template>
        </tr>
        <tr v-if="!hideOilGasRowsForHpsWater">
          <td>% Free Gas at Discharge</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                label=""
                :base-unit="PercentageUnits.Ratio"
                :target-unit="PercentageUnits.Percentage"
                mode="read"
                :model="PercentageUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.individual.value[
                    scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                  ]?.gasAtDischargeRatio
                "
              />
            </td>
          </template>
        </tr>
        <tr>
          <td>% Standard Shaft Load</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                label=""
                :base-unit="PercentageUnits.Ratio"
                :target-unit="PercentageUnits.Percentage"
                mode="read"
                :model="PercentageUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.individual.value[
                    scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                  ]?.standardShaftRatingRatio
                "
              />
            </td>
          </template>
        </tr>
        <tr>
          <td>% High Strength Shaft Load</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]"
                label=""
                :base-unit="PercentageUnits.Ratio"
                :target-unit="PercentageUnits.Percentage"
                mode="read"
                :model="PercentageUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.individual.value[
                    scenario.calcs.descendingOrderPumps.value[pumpIdx - 1]?.id
                  ]?.highStrengthShaftRatingRatio
                "
              />
            </td>
          </template>
        </tr>
        <tr
          v-if="
            pumpIdx <
            Math.max(
              ...scenarios.map(
                scenario => scenario.calcs.descendingOrderPumps.value.length
              )
            ) -
              1
          "
        >
          <td></td>
          <td v-for="j in scenarios.length" :key="j"></td>
        </tr>
        <tr
          v-if="
            pumpIdx <
            Math.max(
              ...scenarios.map(
                scenario => scenario.calcs.descendingOrderPumps.value.length
              )
            )
          "
        >
          <td></td>
          <td v-for="i in scenarios.length * 2" :key="i"></td>
        </tr>
      </template>

      <tr class="title-row">
        <td>Aggregate</td>
        <td v-for="i in scenarios.length * 2" :key="i"></td>
      </tr>

      <tr>
        <td>Max Pressure at Discharge</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                :target-unit="getPressureUnit(pumpDischargePressureInputLabel)"
                mode="read"
                :model="PressureUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.aggregate
                    .totalMaxDischargePressurePSIA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>

      <tr>
        <td>Shut-In Pressure at Discharge</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                :target-unit="
                  getPressureUnit('Shut-In Pressure @ Discharge.convert.PSIA')
                "
                mode="read"
                :model="PressureUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.aggregate
                    .dischargeShutInBurstPressurePSIA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>

      <tr>
        <td>Shut-In Pressure at Wellhead</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getPressureUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                :target-unit="
                  getPressureUnit('Shut-In Pressure @ Wellhead.convert.PSIA')
                "
                mode="read"
                :model="PressureUnits.options()"
                :model-value="
                  scenario.calcs.pumpCalculations.aggregate
                    .wellheadShutInBurstPressurePSIA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
    </table>

    <!-- Intakes -->
    <div class="subheader"></div>
    <table class="report-table">
      <tr class="header-row">
        <th>
          <icon :icon="['far', 'oil-temperature']" />
          <span class="font-medium ml-1 mr-2">{{
            isSizingEspShore ? `Intakes` : `Suction`
          }}</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>{{ scenario.scenario?.title }}</th>
        </template>
      </tr>
      <template
        v-for="intakeIdx in Math.max(
          ...scenarios.map(
            scenario => scenario.calcs.orderedIntakes.value.length
          )
        )"
        :key="intakeIdx"
      >
        <tr>
          <td>Name</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td class="font-semibold">
              <span
                v-if="scenario.calcs.orderedIntakes.value[intakeIdx - 1]"
                class="p-1"
                >{{
                  intakeLabel(
                    scenario.calcs.orderedIntakes.value[intakeIdx - 1].r_intake,
                    scenario.calcs.orderedIntakes.value[intakeIdx - 1]
                      .use_high_strength_shaft
                  )
                }}</span
              >
            </td>
          </template>
        </tr>
        <tr v-if="!isSizingEspShore">
          <td>Pump Inlet Pressure</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-slot="{ getPressureUnit }"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  label=""
                  :target-unit="
                    getPressureUnit('Pump Intake Pressure.convert.PSIA')
                  "
                  :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
                  mode="read"
                  :model="PressureUnits.options()"
                  :model-value="scenario.calcs.pumpIntakePressurePSIA.value"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="isSizingEspShore">
          <td>% Standard Intake Shaft Load</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits :scenario="scenario.scenario">
              <td></td>
              <td>
                <ConverterInput
                  v-if="scenario.calcs.orderedIntakes.value[intakeIdx - 1]"
                  :base-unit="PercentageUnits.Ratio"
                  :target-unit="PercentageUnits.Percentage"
                  :model-value="
                    scenario.calcs?.intakeCalculations.individual.value[
                      scenario.calcs.orderedIntakes.value[intakeIdx - 1]?.id
                    ]?.standardShaftRatingRatio
                  "
                  :model="PercentageUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr v-if="isSizingEspShore">
          <td>% High Strength Intake Shaft Load</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <UseScenarioUnits
              v-if="scenario.calcs.orderedIntakes.value[intakeIdx - 1]"
              :scenario="scenario.scenario"
            >
              <td></td>
              <td>
                <ConverterInput
                  :base-unit="PercentageUnits.Ratio"
                  :target-unit="PercentageUnits.Percentage"
                  :model-value="
                    scenario.calcs?.intakeCalculations.individual.value[
                      scenario.calcs.orderedIntakes.value[intakeIdx - 1]?.id
                    ]?.highStrengthShaftRatingRatio
                  "
                  :model="PercentageUnits.options()"
                  label=""
                  mode="read"
                />
              </td>
            </UseScenarioUnits>
          </template>
        </tr>
        <tr></tr>
      </template>
    </table>

    <!-- Shroud -->
    <div class="subheader"></div>
    <table
      v-if="scenarios.some(scenario => scenario.equipment?.r_shroud)"
      class="report-table"
    >
      <tr class="header-row">
        <th>
          <icon :icon="['fas', 'triangle']" />
          <span class="font-medium ml-1 mr-2">Shroud</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>
            {{ scenario.scenario?.title }}
          </th>
        </template>
      </tr>
      <tr>
        <td>Shroud Size</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLargeLengthUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              {{
                computeCasingLabel(
                  scenario.equipment?.r_shroud,
                  getLargeLengthUnit().value.label === LengthUnits.Meters.label
                )
              }}
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Shroud Length</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getLargeLengthUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                :base-unit="LengthUnits.Feet"
                :target-unit="
                  getLargeLengthUnit('Equipment Length.convert.Feet')
                "
                :model-value="
                  scenario.calcs.equipmentLengths.shroudLengthFt?.value
                "
                :model="LargeLengthUnits.options()"
                label=""
                mode="read"
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
    </table>

    <!-- Seals -->
    <div class="subheader"></div>
    <table class="report-table">
      <tr class="header-row">
        <th>
          <icon :icon="['far', 'oil-temperature']" />
          <span class="font-medium ml-1 mr-2">{{
            isSizingEspShore ? `Seals` : `Thrust Chamber`
          }}</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>{{ scenario.scenario?.title }}</th>
        </template>
      </tr>
      <template
        v-for="sealIdx in Math.max(
          ...scenarios.map(
            scenario => scenario.calcs.descendingOrderSeals.value.length
          )
        )"
        :key="sealIdx"
      >
        <tr>
          <td>Name</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td class="font-semibold">
              <span
                v-if="scenario.calcs.descendingOrderSeals.value[sealIdx - 1]"
                class="p-1"
                >{{
                  computeSealLabel(
                    scenario.calcs.descendingOrderSeals.value[sealIdx - 1]
                      .r_seal,
                    scenario.calcs.descendingOrderSeals.value[sealIdx - 1]
                      .use_high_strength_shaft
                  )
                }}</span
              >
            </td>
          </template>
        </tr>
        <tr>
          <td>
            {{
              isSizingEspShore
                ? `% Standard Shaft Seal Load`
                : `Thrust Load at Duty Point`
            }}
          </td>
          <!-- HPS only ever has one seal -->
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="
                  scenario.calcs.descendingOrderSeals.value[sealIdx - 1] &&
                  isSizingEspShore
                "
                label=""
                :base-unit="PercentageUnits.Ratio"
                :target-unit="PercentageUnits.Percentage"
                mode="read"
                :model="PercentageUnits.options()"
                :model-value="
                  scenario.calcs.sealCalculations.individual.value[
                    scenario.calcs.descendingOrderSeals.value[sealIdx - 1]?.id
                  ]?.standardShaftRatingRatio
                "
              />
              <UseScenarioUnits
                v-else-if="!isSizingEspShore"
                v-slot="{ getMassUnit }"
                :scenario="scenario.scenario"
              >
                <ConverterInput
                  label=""
                  :base-unit="MassUnits.Pounds"
                  :target-unit="getMassUnit()"
                  mode="read"
                  :model="MassUnits.options()"
                  :model-value="
                    scenario?.calcs.pumpCalculations.aggregate.thrustLoadPounds
                      .value
                  "
                />
              </UseScenarioUnits>
            </td>
          </template>
        </tr>
        <tr v-if="isSizingEspShore">
          <td>% High Strength Shaft Seal Load</td>
          <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
            <td></td>
            <td>
              <ConverterInput
                v-if="scenario.calcs.descendingOrderSeals.value[sealIdx - 1]"
                label=""
                :base-unit="PercentageUnits.Ratio"
                :target-unit="PercentageUnits.Percentage"
                mode="read"
                :model="PercentageUnits.options()"
                :model-value="
                  scenario.calcs.sealCalculations.individual.value[
                    scenario.calcs.descendingOrderSeals.value[sealIdx - 1]?.id
                  ]?.highStrengthShaftRatingRatio
                "
              />
            </td>
          </template>
        </tr>
        <tr></tr>
      </template>
    </table>

    <!-- Motor -->
    <div class="subheader"></div>
    <table class="report-table">
      <tr class="header-row">
        <th>
          <icon :icon="['fas', 'engine']" />
          <span class="font-medium ml-1 mr-2">Motor</span>
        </th>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <th></th>
          <th>
            {{ scenario.scenario?.title }}
          </th>
        </template>
      </tr>
      <tr>
        <td>Frequency</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="FrequencyUnits.Hertz"
              mode="read"
              :model="FrequencyUnits.options()"
              :model-value="scenario.calcs.adjustedPumpHzFrequency.value"
            />
          </td>
        </template>
      </tr>
      <tr v-if="isHps">
        <td>Operating RPM</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="GenericUnits.RevolutionsPerMinute"
              mode="read"
              :model="[GenericUnits.RevolutionsPerMinute]"
              :model-value="
                scenario.calcs.pumpCalculations.aggregate.operatingRPM.value
              "
            />
          </td>
        </template>
      </tr>
      <tr>
        <td>
          {{ isHps ? "Operating HP @ Duty Point" : "Operating HP @ Design Hz" }}
        </td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <UseScenarioUnits
            v-slot="{ getHydraulicPowerUnit }"
            :scenario="scenario.scenario"
          >
            <td>
              <ConverterInput
                label=""
                :base-unit="PowerUnits.HorsePower"
                mode="read"
                :model="PowerUnits.options()"
                :target-unit="getHydraulicPowerUnit()"
                :model-value="
                  scenario.calcs.motorCalculations.totalRequiredBrakeHP.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Motor Volts</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getVoltageUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="VoltageUnits.Volts"
                :model="VoltageUnits.options()"
                :target-unit="getVoltageUnit('Total Voltage.convert.V')"
                mode="read"
                :model-value="
                  scenario.calcs.motorCalculations.motorVoltageV.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Operating Current</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="CurrentUnits.Amperes"
              :model="CurrentUnits.options()"
              mode="read"
              :model-value="
                scenario.calcs.motorCalculations.motorCurrentA.value
              "
            />
          </td>
        </template>
      </tr>
      <tr>
        <td>Motor Catalog HP Rating</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <UseScenarioUnits
            v-slot="{ getHydraulicPowerUnit }"
            :scenario="scenario.scenario"
          >
            <td>
              <ConverterInput
                label=""
                :base-unit="PowerUnits.HorsePower"
                mode="read"
                :model="PowerUnits.options()"
                :target-unit="getHydraulicPowerUnit()"
                :model-value="
                  scenario.calcs.motorCalculations.adjustedReratedNameplateHP
                    .value || 0
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Motor Catalog Volts Rating</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <UseScenarioUnits
            v-slot="{ getVoltageUnit }"
            :scenario="scenario.scenario"
          >
            <td>
              <ConverterInput
                label=""
                :base-unit="VoltageUnits.Volts"
                :model="VoltageUnits.options()"
                mode="read"
                :target-unit="getVoltageUnit('Total Voltage.convert.V')"
                :model-value="
                  scenario.calcs.motorCalculations.adjustedReratedNameplateV
                    .value || 0
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>{{ isHps ? "Motor Input HP @ Duty Point" : "Motor Input HP" }}</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <UseScenarioUnits
            v-slot="{ getHydraulicPowerUnit }"
            :scenario="scenario.scenario"
          >
            <td>
              <ConverterInput
                label=""
                :base-unit="PowerUnits.HorsePower"
                mode="read"
                :model="PowerUnits.options()"
                :target-unit="getHydraulicPowerUnit()"
                :model-value="
                  PowerUnits.Watts.toStandardUnit(
                    scenario.calcs.motorCalculations.motorInputW.value
                  )
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Cable Power</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getRealPowerUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PowerUnits.KiloWatts"
                :model="PowerUnits.options()"
                :target-unit="
                  getRealPowerUnit('Total Cables Power Loss.convert.kW')
                "
                mode="read"
                :model-value="
                  scenario.calcs.cableCalculations.aggregate
                    .totalCablesPowerLossKW.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>Motor Load</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="PercentageUnits.Ratio"
              :target-unit="PercentageUnits.Percentage"
              mode="read"
              :model="PercentageUnits.options()"
              :model-value="
                scenario.calcs.motorCalculations.motorLoadRatio.value
              "
            />
          </td>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>% Motor Shaft Load</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="PercentageUnits.Ratio"
              :target-unit="PercentageUnits.Percentage"
              mode="read"
              :model="PercentageUnits.options()"
              :model-value="
                scenario.calcs.motorCalculations.motorShaftRatingRatio.value
              "
            />
          </td>
        </template>
      </tr>
      <tr>
        <td>Required kVA @ Wellhead</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getApparentPowerUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="ApparentPowerUnits.KiloVoltAmps"
                :model="ApparentPowerUnits.options()"
                :target-unit="getApparentPowerUnit('Required kVA.convert.kVA')"
                mode="read"
                :model-value="
                  scenario.calcs.surfaceDriveCalculations.requiredKVA.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>System Power @ Wellhead</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getRealPowerUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="PowerUnits.KiloWatts"
                :model="PowerUnits.options()"
                mode="read"
                :target-unit="getRealPowerUnit('System Power.convert.kW')"
                :model-value="
                  scenario.calcs.surfaceDriveCalculations.systemPowerKW.value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Motor Winding Temp</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getTempUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="TemperatureUnits.Fahrenheit"
                :model="TemperatureUnits.options()"
                :target-unit="
                  getTempUnit('Motor Winding Temperature.convert.F°')
                "
                mode="read"
                :model-value="
                  scenario.calcs.motorCalculations.motorInternalTemperatureF
                    .value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr>
        <td>CO2 emissions (Metric Tons CO2e)</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="MassUnits.MetricTon"
              :model="MassUnits.options()"
              :target-unit="MassUnits.MetricTon"
              mode="read"
              :model-value="
                scenario.calcs.surfaceDriveCalculations.requiredKVA.value *
                0.0004246
              "
            />
          </td>
        </template>
      </tr>
      <tr v-if="isSizingEspShore">
        <td>Flow by Motor</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <UseScenarioUnits
            v-slot="{ getSpeedUnit }"
            :scenario="scenario.scenario"
          >
            <td></td>
            <td>
              <ConverterInput
                label=""
                :base-unit="SpeedUnits.FeetPerSecond"
                mode="read"
                :model="SpeedUnits.options()"
                :target-unit="getSpeedUnit('Flow by Motor.convert.ft/s')"
                :model-value="
                  scenario.calcs.motorCalculations.flowVelocityAroundMotorFTSEC
                    .value
                "
              />
            </td>
          </UseScenarioUnits>
        </template>
      </tr>
      <tr v-if="!isHps">
        <td>Motor Power Factor</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="PercentageUnits.Ratio"
              :target-unit="PercentageUnits.Ratio"
              mode="read"
              :decimal-places="3"
              :model="PercentageUnits.options()"
              :model-value="
                scenario.calcs.motorCalculations.motorPowerFactor.value
              "
            />
          </td>
        </template>
      </tr>
      <tr>
        <td>Motor Efficiency Ratio</td>
        <template v-for="scenario in scenarios" :key="scenario.scenario?.id">
          <td></td>
          <td>
            <ConverterInput
              label=""
              :base-unit="PercentageUnits.Ratio"
              :target-unit="PercentageUnits.Ratio"
              mode="read"
              :model="PercentageUnits.options()"
              :model-value="
                scenario.calcs.motorCalculations.motorEfficiencyRatio.value
              "
            />
          </td>
        </template>
      </tr>
      <tr></tr>
    </table>

    <div v-if="scenariosWithNotes.length" class="notes-section">
      <h3 class="notes-heading">Scenario Notes</h3>
      <hr class="notes-separator" />
      <div
        v-for="scenario in scenariosWithNotes"
        :key="scenario.scenario?.id"
        class="text-sm mb-2"
      >
        <span class="font-semibold">{{ scenario.scenario?.title }}:</span>
        {{ scenario.scenario?.notes }}
      </div>
      <hr class="notes-separator" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ScenarioMap } from "@/components/types";
import type { Sizing } from "@/composables/forms/formTypes";

import { toRefs, computed } from "vue";
import { PressureUnits } from "@/libs/units/pressure";
import { PercentageUnits } from "@/libs/units/percentage";
import { ProductivityIndexUnits } from "@/libs/units/productivityIndex";
import { GasFlowRateUnits, LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { LargeLengthUnits, LengthUnits } from "@/libs/units/length";
import { DensityUnits } from "@/libs/units/density";
import { FrequencyUnits } from "@/libs/units/frequency";
import { MassUnits } from "@/libs/units/mass";
import { PowerUnits } from "@/libs/units/power";
import { VoltageUnits } from "@/libs/units/voltage";
import { CurrentUnits } from "@/libs/units/current";
import { ApparentPowerUnits } from "@/libs/units/apparentPower";
import { TemperatureUnits } from "@/libs/units/temperature";
import { computeCasingLabel } from "@/models/equipment/casings/properties";
import { computeTubingLabel } from "@/models/equipment/tubings/properties";
import { computeYToolLabel } from "@/models/equipment/y_tool/properties";
import {
  computeBoltOnDischargeLabel,
  isBoltOnDischargeMeasurementKnown
} from "@/models/equipment/bolt_on_discharges/properties";
import {
  computePumpLabel,
  computePumpStageType
} from "@/models/equipment/pumps/properties";
import { GenericUnits } from "@/libs/units/generic";
import ConverterInput from "@/components/ConverterInput.vue";
import ReportSectionHeader from "@/components/reports/ReportSectionHeader.vue";
import UseScenarioUnits from "@/composables/units/UseScenarioUnits.vue";
import { SpeedUnits } from "@/libs/units/speed";
import { computeIntakeLabel } from "@/models/equipment/intake/properties";
import { computeIntakeLabel as computeHpsIntakeLabel } from "@/models/equipment/hps_intake/properties";
import { computeSealLabel } from "@/models/equipment/seals/properties";
import { ConcentrationUnits } from "@/libs/units/concentration";
import { shouldShowOilRates } from "@/utils/scenarioUtils";
import {
  getHpsOilApiLabel,
  getHpsSingleLiquidRateLabel,
  getHpsTotalLiquidRateLabel,
  getHpsWaterGravityLabel,
  isHpsCondensateLiquidType,
  isHpsOilLiquidType,
  isHpsWaterLiquidType,
  shouldHideHpsOilRateRow,
  shouldHideHpsWaterCutRow
} from "@/utils/hpsLiquidType";
import { Sizing_Pumping_Type_Enum, useDisplayUnitsQuery } from "gql/api";

const { data: displayUnits } = useDisplayUnitsQuery();
const props = defineProps<{
  scenarios: Array<ScenarioMap[string]>;
  sizing: Sizing | null | undefined;
  removePageBreak?: boolean;
}>();
const { sizing } = toRefs(props);
const dateGenerated = Intl.DateTimeFormat("en-us").format(new Date());

const scenariosWithNotes = computed(() =>
  props.scenarios.filter(s => s.scenario?.notes)
);

const isSizingEspShore = computed(() => {
  return props.sizing?.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore;
});

const isHps = computed(() => {
  return props.sizing?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

const headerLocationOrWellName = computed(() =>
  isHps.value
    ? props.sizing?.hps_location ?? null
    : props.sizing?.well?.well_name
);

function intakeLabel(
  rIntake: unknown,
  useHighStrengthShaft: boolean | null | undefined
) {
  return isHps.value
    ? computeHpsIntakeLabel(
        rIntake as Parameters<typeof computeHpsIntakeLabel>[0],
        useHighStrengthShaft
      )
    : computeIntakeLabel(
        rIntake as Parameters<typeof computeIntakeLabel>[0],
        useHighStrengthShaft
      );
}

const pumpDischargePressureInputLabel = computed(() => {
  return isSizingEspShore.value
    ? "Produced Discharge Pressure.convert.PSIA"
    : "HPS Required Discharge Pressure [Prd].write.PSIA";
});

const targetLiquidRateInputLabel = computed(() => {
  return isSizingEspShore.value
    ? "Target Liquid Rate.convert.STB/D"
    : "Total Liquid Rate.write.STB/D";
});

const gasDisplayFormattScenarioMapedScenarios = computed(() => {
  return props.scenarios.map(scenario => {
    return {
      scenario,
      showOilRates: shouldShowOilRates(
        displayUnits.value,
        scenario?.scenario || null
      )
    };
  });
});

const hideOilGasRowsForHpsWater = computed(
  () =>
    isHps.value &&
    props.scenarios.some(scenario =>
      isHpsWaterLiquidType(scenario.wellProperties?.hps_liquid_type)
    )
);

const hideWaterRowsForHpsOil = computed(
  () =>
    isHps.value &&
    props.scenarios.some(
      scenario =>
        isHpsOilLiquidType(scenario.wellProperties?.hps_liquid_type) ||
        isHpsCondensateLiquidType(scenario.wellProperties?.hps_liquid_type)
    )
);

const hideWaterCutRowsForHpsAmine = computed(
  () =>
    isHps.value &&
    props.scenarios.some(scenario =>
      shouldHideHpsWaterCutRow(scenario.wellProperties?.hps_liquid_type)
    )
);

const hideOilRateRowsForHpsCondensate = computed(
  () =>
    isHps.value &&
    props.scenarios.some(scenario =>
      shouldHideHpsOilRateRow(scenario.wellProperties?.hps_liquid_type)
    )
);

function getPumpBepRatio(
  scenario: ScenarioMap[string],
  pumpIdx: number
): number | undefined {
  const pump = scenario.calcs.descendingOrderPumps.value[pumpIdx - 1];
  if (!pump) return undefined;
  const pumpCalc = scenario.calcs.pumpCalculations.individual.value[pump.id];
  const rorBep = pumpCalc?.rorBep;
  if (rorBep === undefined || rorBep <= 0) return undefined;
  const intakeFlowBBLD =
    pumpCalc?.intakeStageCalculations?.intakeFluidPropertiesContext
      .flowRatesInSitu?.fluidFlowRateBBLD;
  if (intakeFlowBBLD === undefined || intakeFlowBBLD <= 0) return undefined;
  return intakeFlowBBLD / rorBep;
}
</script>

<style lang="scss" scoped>
.svg-inline--fa {
  color: var(--danger-500);
}
.report-table {
  margin-bottom: 40px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  &:not(:first-of-type) {
    break-inside: avoid;
  }
}

tr {
  break-inside: avoid;
}

// every cell on the page
tr > * {
  padding-left: 10px;
  height: 26px;
}

//first cell in first row of each table
th:first-of-type {
  padding-left: 0;
  font-size: 12px;
  color: var(--gray-900);
  width: 190px;
}

// not well-info-table ¯\_(ツ)_/¯
table:not(.well-info-table) {
  tr {
    height: 26px;
  }
  tr:nth-of-type(2n) {
    background-color: $hover-color;
  }
}

// scenario value column
td:nth-of-type(2n + 3) {
  background-color: rgba(33, 150, 243, 0.05);
}

// spacer column
th,
td {
  &:nth-of-type(2n + 2) {
    width: 20px;
  }
}

// first row of each table
.header-row {
  // scenario value column
  th:nth-of-type(2n + 3) {
    background: var(--gray-400);
  }
  // first cell in first row
  td:first-of-type {
    font-size: 14px;
    padding-left: 0;
    font-size: 12px;
    color: var(--gray-900);
  }
}

// all rows that are data content
tr:not(.header-row):not(.title-row) {
  font-weight: 300;
  & > td:not(:first-of-type) {
    font-weight: 500;
  }
}

// well info section headers
tr.title-row {
  background-color: $hover-color;
  // height: 24px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.5px;
}

@media screen {
  html[data-theme="dark"] {
    .report-table tr {
      &:nth-child(even) {
        background-color: var(--surface-100);
      }
      &:nth-child(odd) {
        background-color: black;
      }
      color: white !important;
    }
  }
}

.notes-section {
  margin-top: 2rem;
}

.notes-heading {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.notes-separator {
  margin: 0.5rem 0 1rem 0;
  border: none;
  border-top: 1px solid var(--surface-300);
}
</style>
